/**
 * Insights iframe module creation and resize handling
 * @module lib/insights/iframe
 */

const { createIframeResizer } = require('../utils/iframeResizer');
const { buildMillaAuthUrl } = require('../utils/millaAuth');
const { buildInsightsIframeUrl, buildPredictionIframeUrl, resolveInsightsArticleId } = require('./urls');
const { readInsightsExposureContext } = require('./exposure');
const { readStoredAnonId, storeAnonId, adoptAnonId } = require('./anonSync');
const { studyImpressionSignal } = require('./study');
const { prepareInsightsIdentity } = require('../identity');

const INSIGHTS_IFRAME_MIN_HEIGHT = 320;

const ensureInsightsIframeResizer = createIframeResizer({
  messageType: 'MILLA_INSIGHTS_RESIZE',
  minHeight: INSIGHTS_IFRAME_MIN_HEIGHT,
  // Generous ceiling: long multi-select steps plus the captcha and invite
  // panel can exceed 1800px on narrow mobile viewports, which cropped the
  // Next/Submit row.
  maxHeight: 2400
});

function resolveLanguage() {
  if (typeof window === 'undefined') return 'en';
  const lang = window.fwjson?.lang || document.documentElement?.lang || '';
  return String(lang).toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

async function createInsightsImpression(options) {
  const brand = options.brand === 'mtlblog' ? 'mtlblog' : 'narcity';
  if (!await prepareInsightsIdentity(brand)) return null;
  const storedAnon = readStoredAnonId(brand);
  if (storedAnon) {
    await adoptAnonId(brand, storedAnon);
  }

  const exposureContext = readInsightsExposureContext(options.insights, {
    surveyId: options.surveyId,
    surveyType: options.surveyType,
    articleId: options.articleId,
    brand,
    language: options.language
  });
  // A brand lift study survey carries the reader's study signal, re-read for
  // the latest counts. The server re-derives the bucket and refuses a
  // mismatch, so a reader who lost the group since selection gets no widget.
  const studySignal = options.studyKey ? studyImpressionSignal(brand, options.studyKey) : null;
  if (options.studyKey && !studySignal) return null;
  const body = {
    surveyId: options.surveyId,
    anonId: storedAnon || undefined,
    brand,
    surveyType: options.surveyType || undefined,
    articleId: options.articleId || undefined,
    sourceUrl: typeof window !== 'undefined' && window.location ? window.location.href : undefined,
    exposure: studySignal ? studySignal.exposure : exposureContext.exposure,
    exposureEligible: studySignal ? true : exposureContext.exposureEligible,
    exposureCampaignId: studySignal
      ? studySignal.exposureCampaignId
      : (exposureContext.exposureCampaignId || undefined),
    studyContext: studySignal ? studySignal.studyContext : undefined
  };

  const response = await fetch(buildMillaAuthUrl('/api/insights/impression'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || data.success === false || !data.impression?.id) {
    return null;
  }
  if (data.anonId) {
    storeAnonId(brand, data.anonId);
  }
  return {
    impressionId: data.impression.id,
    anonId: data.anonId || storedAnon || '',
    exposureGroup: data.impression.exposureGroup || null,
    exposureSource: data.impression.exposureSource || null
  };
}

async function sendInsightsViewable(options) {
  if (!options?.surveyId || !options?.impressionId) return null;
  try {
    const response = await fetch(buildMillaAuthUrl('/api/insights/impression/viewable'), {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        surveyId: options.surveyId,
        impressionId: options.impressionId,
        brand: options.brand,
        exposureGroup: options.exposureGroup || undefined,
        anonId: options.anonId || undefined
      })
    });
    return response.json().catch(() => null);
  } catch (_) {
    return null;
  }
}

function observeInsightsViewability(wrapper, options) {
  if (
    typeof window === 'undefined' ||
    typeof window.IntersectionObserver !== 'function' ||
    !wrapper ||
    !options?.surveyId ||
    !options?.impressionId
  ) {
    return;
  }

  let sent = false;
  let timer = null;
  let observer = null;

  const clearTimer = () => {
    if (timer) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const sendOnce = () => {
    if (sent) return;
    sent = true;
    clearTimer();
    if (observer) observer.disconnect();
    sendInsightsViewable(options);
  };

  observer = new window.IntersectionObserver((entries) => {
    if (sent) return;
    const entry = entries.find((candidate) => candidate.target === wrapper) || entries[0];
    const isViewable = Boolean(entry?.isIntersecting) && Number(entry.intersectionRatio || 0) >= 0.5;
    if (isViewable) {
      if (!timer) {
        timer = window.setTimeout(sendOnce, 1000);
      }
      return;
    }
    clearTimer();
  }, { threshold: [0, 0.5, 1] });

  window.setTimeout(() => {
    if (!sent) observer.observe(wrapper);
  }, 0);
}

async function createInsightsIframeModule(options = {}) {
  if (typeof document === 'undefined') return null;
  ensureInsightsIframeResizer();

  const surveyId = typeof options.surveyId === 'string' ? options.surveyId.trim() : '';
  if (!surveyId) return null;

  const articleId = resolveInsightsArticleId(options.articleId);
  const location = typeof options.location === 'string' && options.location.trim()
    ? options.location.trim()
    : 'article_end';
  const brand = options.brand === 'mtlblog' ? 'mtlblog' : 'narcity';
  const language = options.language === 'fr' ? 'fr' : resolveLanguage();

  const impression = await createInsightsImpression({
    surveyId,
    articleId,
    brand,
    language,
    surveyType: options.surveyType,
    insights: options.insights,
    studyKey: typeof options.studyKey === 'string' ? options.studyKey : ''
  });
  if (!impression) return null;

  const iframeUrl = buildInsightsIframeUrl(surveyId, {
    articleId,
    location,
    language,
    brand,
    impressionId: impression.impressionId
  });
  if (!iframeUrl) return null;

  const iframe = document.createElement('iframe');
  iframe.src = iframeUrl;
  iframe.loading = 'lazy';
  iframe.title = language === 'fr' ? 'Sondage Narcity Insights' : 'Narcity Insights survey';
  iframe.setAttribute('scrolling', 'no');
  iframe.style.border = '0';
  iframe.style.width = '100%';
  iframe.style.height = `${INSIGHTS_IFRAME_MIN_HEIGHT}px`;
  iframe.style.overflow = 'hidden';

  const wrapper = document.createElement('div');
  wrapper.style.width = '100%';
  wrapper.style.margin = location.startsWith('article') ? '16px 0' : '0 0 16px 0';
  wrapper.style.minHeight = `${INSIGHTS_IFRAME_MIN_HEIGHT}px`;
  wrapper.style.fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  wrapper.appendChild(iframe);
  observeInsightsViewability(wrapper, {
    surveyId,
    impressionId: impression.impressionId,
    brand,
    exposureGroup: impression.exposureGroup,
    anonId: impression.anonId
  });
  return wrapper;
}

async function createPredictionIframeModule(options = {}) {
  if (typeof document === 'undefined') return null;
  ensureInsightsIframeResizer();
  const publicSlug = typeof options.publicSlug === 'string' ? options.publicSlug.trim() : '';
  const predictionId = typeof options.predictionId === 'string' ? options.predictionId.trim() : '';
  if (!publicSlug || !predictionId) return null;
  const brand = options.brand === 'mtlblog' ? 'mtlblog' : 'narcity';
  const language = options.language === 'fr' ? 'fr' : resolveLanguage();
  const articleId = resolveInsightsArticleId(options.articleId);
  if (!await prepareInsightsIdentity(brand)) return null;
  const storedAnon = readStoredAnonId(brand);
  if (storedAnon) await adoptAnonId(brand, storedAnon, { publicInsights: true });
  const iframeUrl = buildPredictionIframeUrl(publicSlug, { articleId, language, brand });
  if (!iframeUrl) return null;

  const iframe = document.createElement('iframe');
  iframe.src = iframeUrl;
  iframe.loading = 'lazy';
  iframe.title = language === 'fr' ? 'Prédiction Narcity Insights' : 'Narcity Insights Prediction';
  iframe.setAttribute('scrolling', 'no');
  iframe.setAttribute('allow', 'clipboard-write');
  iframe.style.border = '0';
  iframe.style.width = '100%';
  iframe.style.height = '720px';
  iframe.style.overflow = 'hidden';

  const wrapper = document.createElement('div');
  wrapper.style.width = '100%';
  wrapper.style.margin = '16px 0';
  wrapper.style.minHeight = '560px';
  wrapper.style.fontFamily = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  wrapper.appendChild(iframe);

  if (typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function') {
    const sendVisibility = (viewable) => {
      iframe.contentWindow?.postMessage({
        type: 'MILLA_PREDICTION_VISIBILITY',
        predictionId,
        viewable
      }, '*');
    };
    const observer = new window.IntersectionObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === wrapper) || entries[0];
      sendVisibility(Boolean(entry?.isIntersecting) && Number(entry?.intersectionRatio || 0) >= 0.25);
    }, { rootMargin: '600px 0px', threshold: [0, 0.25, 0.5, 1] });
    window.setTimeout(() => observer.observe(wrapper), 0);
  }
  return wrapper;
}

module.exports = {
  INSIGHTS_IFRAME_MIN_HEIGHT,
  ensureInsightsIframeResizer,
  createInsightsIframeModule,
  createPredictionIframeModule
};
